/**
 * WebView 메시지 프로토콜 버전
 * - 메이저 버전 변경: 호환성 없는 변경 (breaking changes)
 * - 마이너 버전 변경: 하위 호환 가능한 기능 추가
 * - 패치 버전 변경: 버그 수정
 */
export const MESSAGE_PROTOCOL_VERSION = '1.1.0';

/**
 * 지원하는 최소 프로토콜 버전
 * 이 버전 미만의 메시지는 경고를 표시
 */
export const MIN_SUPPORTED_VERSION = '1.0.0';

// 메시지 타입 상수
export const MESSAGE_TYPES = {
  AUTH_VERIFICATION: 'AUTH_VERIFICATION',
  CRAVING_CONTENTS: 'CRAVING_CONTENTS',
  ALERT: 'ALERT',
  DEVICE_TOKEN: 'DEVICE_TOKEN',
  NAVIGATION_GUARD: 'NAVIGATION_GUARD',
  WIDGET_DATA: 'WIDGET_DATA',
  NAVIGATE: 'NAVIGATE',
  LANGUAGE_CHANGE: 'LANGUAGE_CHANGE',
  OPEN_STORE: 'OPEN_STORE',
} as const;

// 본인인증
type AuthVerificationMessage = {
  type: typeof MESSAGE_TYPES.AUTH_VERIFICATION;
  payload: {
    verifyUrl: string;
  };
};

// 갈망 SOS
type CravingContentsMessage = {
  type: typeof MESSAGE_TYPES.CRAVING_CONTENTS;
  payload: {
    title: string;
    contentsUrl: string;
  };
};

// 알림창
type AlertMessage = {
  type: typeof MESSAGE_TYPES.ALERT;
  payload: {
    isOpen: boolean;
  };
};

// 디바이스 토큰 api 요청
type DeviceTokenRequestMessage = {
  type: typeof MESSAGE_TYPES.DEVICE_TOKEN;
};

// 디바이스 토큰 app 응답
type DeviceTokenResponseMessage = {
  type: typeof MESSAGE_TYPES.DEVICE_TOKEN;
  payload: {
    deviceToken: string;
  };
};

// 백버튼 방지
type NavigationGuardMessage = {
  type: typeof MESSAGE_TYPES.NAVIGATION_GUARD;
  payload: {
    preventNavigation: boolean;
  };
};

// 위젯 데이터
export type CravingForecastWidgetData = {
  morningRisk: 'safe' | 'warning' | 'danger';
  afternoonRisk: 'safe' | 'warning' | 'danger';
  peakTime: string | null;
};

export type WidgetDataPayload = {
  isLoggedIn: boolean; // 로그인 상태
  userNm?: string; // 사용자 이름
  cravingForecast?: CravingForecastWidgetData; // 갈망 예보 데이터
  accessToken?: string; // 백그라운드 위젯 갱신용 토큰
  refreshToken?: string; // 백그라운드 위젯 갱신용 리프레시 토큰
  showWidgetGuide?: boolean; // 위젯 설치 안내 표시 (회원가입 후 온보딩 완료 시 최초 1회)
};

type WidgetDataMessage = {
  type: typeof MESSAGE_TYPES.WIDGET_DATA;
  payload: WidgetDataPayload;
};

// 딥링크 네비게이션 (App → Web)
// target: 키워드(mission 등) 또는 경로 문자열(/drJin 등)
export type NavigateMessage = {
  type: typeof MESSAGE_TYPES.NAVIGATE;
  payload: {
    link: string;
    contentLinks?: string;
    body?: string;
    situation?: string;
    condition?: string;
  };
};

/**
 * 메시지 기본 구조 (버전 포함)
 */
export interface BaseMessage {
  type: string;
  /** 프로토콜 버전 (예: "1.0.0") */
  version?: string;
}

// @MX:NOTE: [AUTO] AelrtMessage -> AlertMessage 오타 수정, 하위 호환을 위한 별칭 유지
// @MX:SPEC: SPEC-WEBVIEW-001
type AelrtMessage = AlertMessage;

// 언어 변경 메시지 (Web → App)
type LanguageChangeMessage = {
  type: typeof MESSAGE_TYPES.LANGUAGE_CHANGE;
  payload: {
    language: string;
  };
};

// 스토어 이동 메시지 (Web → App)
type OpenStoreMessage = {
  type: typeof MESSAGE_TYPES.OPEN_STORE;
  payload: {
    storeUrl: string;
  };
};

// 보내는 메시지 타입 (버전 필드 포함)
export type SendMessageType = BaseMessage &
  (
    | AuthVerificationMessage
    | CravingContentsMessage
    | AlertMessage
    | NavigationGuardMessage
    | DeviceTokenRequestMessage
    | WidgetDataMessage
    | LanguageChangeMessage
    | OpenStoreMessage
  );

// 본인인증 응답 (App → Web)
export type AuthVerificationResponseMessage = {
  resultCode: 'S' | 'F';
  statusCode?: string | number;
  data?: unknown;
  errorMessage?: string;
};

// 받는 메시지 타입
export type ReceiveMessageType =
  | AlertMessage
  | DeviceTokenResponseMessage
  | NavigateMessage
  | AuthVerificationResponseMessage;
